import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { api, type DevicesView, type ReleaseView, type Subscription } from '../api.js'
import { date, megabytes, money, relativeDays } from '../format.js'
import { Alert, Card, Consequences, Pill, Row, Rows, StatusPill } from '../components/ui.js'
import { messageFor } from './Register.js'
import { consequencesOf } from '../subscription.js'

export function Overview() {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [devices, setDevices] = useState<DevicesView | null>(null)
  const [release, setRelease] = useState<ReleaseView | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void Promise.all([api.subscription(), api.devices(), api.currentRelease()])
      .then(([s, d, r]) => {
        setSubscription(s)
        setDevices(d)
        setRelease(r)
      })
      .catch((err: unknown) => setError(messageFor(err)))
  }, [])

  if (error) return <Alert>{error}</Alert>
  if (!subscription || !devices) return <p className="empty">Loading…</p>

  const active = subscription.status === 'active'
  const trial = subscription.status === 'trial'

  return (
    <>
      <Card title="Subscription" action={<StatusPill subscription={subscription} />}>
        {active && subscription.daysRemaining !== null ? (
          <p>
            Paid through <strong>{date(subscription.paidThrough)}</strong> —{' '}
            {relativeDays(subscription.daysRemaining, false)} left.
          </p>
        ) : null}
        {subscription.status === 'lapsed' ? (
          <p>
            Lapsed {relativeDays(subscription.daysLapsed ?? 0, true)}. The till has stopped
            selling. Your sales are safe and still syncing.
          </p>
        ) : null}
        {subscription.status === 'unpaid' ? (
          <p>Pay for your first month to download the till software and set up a device.</p>
        ) : null}
        {trial && subscription.trialDaysRemaining !== null ? (
          <p>
            Free trial — <strong>{subscription.trialDaysRemaining}</strong> day
            {subscription.trialDaysRemaining === 1 ? '' : 's'} left.
          </p>
        ) : null}

        {subscription.status !== 'active' && !trial ? (
          // The same lists the Billing screen shows, from the same function,
          // so the two pages cannot tell an owner different things about one
          // state — the reassuring half is the part that gets dropped.
          <Consequences {...consequencesOf(subscription)} />
        ) : null}

        <Rows>
          <Row label="Plan">{money(subscription.priceCents)} per month</Row>
          <Row label="Devices included">{subscription.deviceAllowance}</Row>
        </Rows>

        <div className="actions">
          <Link to="/billing">
            <button type="button" className={active ? '' : 'primary'}>
              {active ? 'Billing' : 'Pay now'}
            </button>
          </Link>
        </div>
      </Card>

      <Card
        title="Till software"
        note="The same installer serves every business. Install it on the shop PC and run it."
        action={release?.release ? <Pill tone="muted">{release.release.version}</Pill> : undefined}
      >
        {!release?.release ? (
          <p className="empty">No release has been published yet. This is where the installer will appear.</p>
        ) : !release.canDownload ? (
          <p className="empty">Available once your subscription is active.</p>
        ) : (
          <>
            {release.release.notes ? <p>{release.release.notes}</p> : null}
            <Rows>
              <Row label="Version">{release.release.version}</Row>
              <Row label="Size">{megabytes(release.release.sizeBytes)}</Row>
              <Row label="Released">{date(release.release.releasedAt)}</Row>
            </Rows>
            <div className="actions">
              <Link to="/download">
                <button type="button" className="primary">Download installer</button>
              </Link>
            </div>
          </>
        )}
      </Card>

      <Card
        title="Devices"
        action={
          devices.allowance.canEnrolAnother ? (
            <Pill tone="muted">{devices.allowance.used} of {devices.allowance.limit}</Pill>
          ) : (
            <Pill tone="ok">{devices.allowance.used} of {devices.allowance.limit}</Pill>
          )
        }
      >
        {devices.devices.filter((d) => d.active).length === 0 ? (
          <p className="empty">No device set up yet.</p>
        ) : (
          <p>Your till is set up. Manage it on the Devices page.</p>
        )}
        {devices.allowance.canEnrolAnother ? (
          <p className="card__note" style={{ margin: 0 }}>
            Run the installer on the shop PC and sign in there to set one up.
          </p>
        ) : null}
        <div className="actions">
          <Link to="/devices"><button type="button">Devices</button></Link>
        </div>
      </Card>
    </>
  )
}
